import { useEffect, useState, MutableRefObject, useRef } from 'react';
import { eventStreamService } from 'core-roblox-utilities';
import parsingUtils from '../utils/parsingUtils';
import { debounce } from '../utils/helperUtils';
import { common } from '../constants/configConstants';
import eventStreamConstants, { TGridGameImpressions } from '../constants/eventStreamConstants';

export type TBuildGridGameImpressionsEventProperties = (
  impressedIndex: number[]
) => TGridGameImpressions | undefined;

export type TCalculateViewedGameData = (
  isEmphasis: boolean,
  width: number,
  height: number,
  offset: number,
  tileWidth?: number,
  tileHeight?: number
) => [number, number] | undefined;

const gridProperties = {
  paddingSize: 10,
  featureTileDifference: 2 - 1 // Feature Tile Size : Tile Size
};

const gridCalculationViewedGameData: TCalculateViewedGameData = (
  hasEmphasis: boolean,
  width: number,
  height: number,
  offset: number,
  tileWidth?: number,
  tileHeight?: number
) => {
  if (tileWidth && tileHeight) {
    const numberOfTilesPerRow = Math.floor((width - gridProperties.paddingSize * 2) / tileWidth);
    const numberOfUnseenRows = Math.round(offset / tileHeight);
    const numberOfViewedRows = Math.round((offset + height) / tileHeight);

    let seenStartIndex = numberOfTilesPerRow * numberOfUnseenRows;
    seenStartIndex = hasEmphasis
      ? Math.max(seenStartIndex - gridProperties.featureTileDifference, 0)
      : seenStartIndex;

    let seenEndIndex = numberOfTilesPerRow * numberOfViewedRows;
    seenEndIndex = hasEmphasis ? seenEndIndex - gridProperties.featureTileDifference : seenEndIndex;
    return [seenStartIndex, seenEndIndex];
  }
  return undefined;
};

export const grid = {
  ...gridProperties,
  calculateViewedGameData: gridCalculationViewedGameData
};

const newGridProperties = {
  paddingSize: 12,
  horizontalGutterSize: 12,
  verticalGutterSize: 24,
  featureTileDifference: 4 - 1 // Feature Tile Size : Tile Size
};

const newGridCalculationViewedGameData: TCalculateViewedGameData = (
  hasEmphasis: boolean,
  width: number,
  height: number,
  offset: number,
  tileWidth?: number,
  tileHeight?: number
) => {
  if (tileWidth && tileHeight) {
    const numberOfTilesPerRow = Math.floor(
      (width - newGridProperties.paddingSize) / (tileWidth + newGridProperties.horizontalGutterSize)
    );
    const numberOfUnseenRows = Math.round(
      (offset - newGridProperties.paddingSize) / (tileHeight + newGridProperties.verticalGutterSize)
    );
    const numberOfViewedRows = Math.round(
      (offset + height - newGridProperties.paddingSize) /
        (tileHeight + newGridProperties.verticalGutterSize)
    );

    let seenStartIndex = numberOfTilesPerRow * numberOfUnseenRows;
    seenStartIndex = hasEmphasis
      ? Math.max(seenStartIndex - newGridProperties.featureTileDifference, 0)
      : seenStartIndex;

    let seenEndIndex = numberOfTilesPerRow * numberOfViewedRows;
    seenEndIndex = hasEmphasis
      ? seenEndIndex - newGridProperties.featureTileDifference
      : seenEndIndex;

    return [seenStartIndex, seenEndIndex];
  }
  return undefined;
};

export const newGrid = {
  ...newGridProperties,
  calculateViewedGameData: newGridCalculationViewedGameData
};

export const useGameImpressionGridTracker = <T extends HTMLElement>(
  tileRef: MutableRefObject<T | null>,
  hasEmphasis: boolean,
  buildEventProperties: TBuildGridGameImpressionsEventProperties,
  calculateViewedGameData: TCalculateViewedGameData
): [number, number, number] => {
  const [width, setWidth] = useState<number>(window.innerWidth);
  const [height, setHeight] = useState<number>(window.innerHeight);
  const [offset, setOffset] = useState<number>(window.pageYOffset);
  const hasEmphasisRef = useRef<boolean>(hasEmphasis);
  const buildEventPropertiesRef = useRef<TBuildGridGameImpressionsEventProperties>(
    buildEventProperties
  );
  const widthRef = useRef<number>(width);
  const heightRef = useRef<number>(height);
  const offsetRef = useRef<number>(offset);
  const impressedRangeRef = useRef<[number, number] | undefined>(undefined);

  const updateImpressedRange = (sortedImpressedIndexes: number[]): void => {
    if (impressedRangeRef.current === undefined) {
      impressedRangeRef.current = [
        sortedImpressedIndexes[0],
        sortedImpressedIndexes[sortedImpressedIndexes.length - 1]
      ];
    } else {
      const minRange =
        impressedRangeRef.current[0] < sortedImpressedIndexes[0]
          ? impressedRangeRef.current[0]
          : sortedImpressedIndexes[0];
      const maxRange =
        impressedRangeRef.current[1] > sortedImpressedIndexes[sortedImpressedIndexes.length - 1]
          ? impressedRangeRef.current[1]
          : sortedImpressedIndexes[sortedImpressedIndexes.length - 1];
      impressedRangeRef.current = [minRange, maxRange];
    }
  };

  const sendEvent = () => {
    // Calculate visibility
    let sortedImpressedIndexes: number[] = [];
    const possibleGridVisibleTileRange = calculateViewedGameData(
      hasEmphasisRef.current,
      widthRef.current,
      heightRef.current,
      offsetRef.current,
      tileRef.current?.clientWidth,
      tileRef.current?.clientHeight
    );

    // Retrieve Impressions Intersections
    if (possibleGridVisibleTileRange) {
      sortedImpressedIndexes = parsingUtils.calculateImpressedIndexes(
        impressedRangeRef.current,
        possibleGridVisibleTileRange
      );
    }

    // Trigger Event only where there are Unseen Tiles
    parsingUtils
      .splitArray(sortedImpressedIndexes, common.maxTilesInGameImpressionsEvent)
      .forEach(impressedIndexes => {
        const gameImpressionsParams =
          impressedIndexes.length !== 0
            ? buildEventPropertiesRef.current(impressedIndexes)
            : undefined;

        if (gameImpressionsParams !== undefined && gameImpressionsParams.absPositions.length > 0) {
          updateImpressedRange(gameImpressionsParams.absPositions);
          const eventStreamParams = eventStreamConstants.gameImpressions(gameImpressionsParams);
          eventStreamService.sendEvent(...eventStreamParams);
        }
      });
  };

  const [onScroll, cancelOnScroll] = debounce(() => {
    offsetRef.current = window.pageYOffset;
    heightRef.current = window.innerHeight;
    setOffset(window.pageYOffset);
    setHeight(window.innerHeight);
    sendEvent();
  });

  const [onResize, cancelOnResize] = debounce(() => {
    widthRef.current = window.innerWidth;
    heightRef.current = window.innerHeight;
    offsetRef.current = window.pageYOffset;
    setOffset(window.pageYOffset);
    setHeight(window.innerHeight);
    setWidth(window.innerWidth);
    sendEvent();
  });

  const onCleanUp = () => {
    cancelOnScroll();
    cancelOnResize();
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('resize', onResize);
  };

  useEffect(() => {
    window.addEventListener('scroll', onScroll);
    window.addEventListener('resize', onResize);
    return onCleanUp;
  }, []);

  useEffect(() => {
    hasEmphasisRef.current = hasEmphasis;
    buildEventPropertiesRef.current = buildEventProperties;
    sendEvent();
  }, [buildEventProperties, hasEmphasis]);

  return [height, width, offset];
};

export default {
  newGrid,
  grid,
  useGameImpressionGridTracker
};
